import {
    ClientCookieManager,
    COOKIE_NAME_CONSENT_STATUS,
    getCookieValue,
} from 'dibs-consented-storage';
import { DIBS_SESSION } from './helpers/constants.ts';
import { hasConsentForGroups, type ConsentGroup } from './helpers/consent.ts';

// 30 minutes in milliseconds
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Each activity reset rewrites localStorage and the cookie, so cap it well under the timeout.
const ACTIVITY_THROTTLE = 60 * 1000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'touchstart', 'popstate'] as const;

/**
 * The session id is a first-party analytics/measurement id (joined across GA4 and Datadog RUM),
 * so it follows the Performance category. It is not shared with ad platforms, so Targeting is not
 * required.
 */
const DIBS_SESSION_CONSENT_GROUPS: readonly ConsentGroup[] = ['C0002'];

type CampaignParams = {
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    utm_content: string | null;
    utm_term: string | null;
};

type SessionInfo = {
    sessionId: string;
    lastActivity: number;
    campaign: CampaignParams;
    landingSite?: string;
};

// dibs-fastly's validateSessionId requires exactly 12 digits here (see SESSION_ID_RANDOM_NUM_LENGTH).
const SESSION_ID_RANDOM_NUM_LENGTH = 12;
const SESSION_ID_RANDOM_NUM_MAX = 10 ** SESSION_ID_RANDOM_NUM_LENGTH;

// Matches generateSessionId()'s <12-digit random>.<13-digit epoch ms> shape.
const SESSION_ID_PATTERN = /^[0-9]{12}\.[0-9]{13}$/;

/**
 * Generate a unique session ID
 * @returns {string} A unique session ID
 */
function generateSessionId(): string {
    // Math.random().toString() can be shorter than 12 digits (trailing zeros are dropped) or
    // switch to exponential notation for very small values, so build the digits directly instead.
    const randomPart = Math.floor(Math.random() * SESSION_ID_RANDOM_NUM_MAX)
        .toString()
        .padStart(SESSION_ID_RANDOM_NUM_LENGTH, '0');
    return `${randomPart}.${Date.now()}`;
}

function getCurrentLandingSite(): string {
    return `${window.location.pathname}${window.location.search}`;
}

/**
 * Set the session cookie
 * @param {SessionInfo} info - The session info
 */
function setSessionCookie(info: SessionInfo): void {
    const {
        utm_source: s,
        utm_medium: m,
        utm_campaign: ca,
        utm_content: co,
        utm_term: t,
    } = info?.campaign || {};
    const value = `${info.sessionId}|${info.lastActivity}|${JSON.stringify({ s, m, ca, co, t })}`;
    const expires = new Date(Date.now() + SESSION_TIMEOUT);
    ClientCookieManager.set(DIBS_SESSION, value, { expires });
}

/**
 * Get the campaign params from the URL
 * @returns {CampaignParams} The campaign params
 */
function getCampaignParams(): CampaignParams {
    const params = new URLSearchParams(window.location.search);
    return {
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        utm_content: params.get('utm_content'),
        utm_term: params.get('utm_term'),
    };
}

/**
 * Check if the campaign has changed
 * @param {CampaignParams} prev - The previous campaign params from localStorage
 * @param {CampaignParams} curr - The current campaign params from the URL
 * @returns {boolean} True if the campaign has changed, false otherwise
 */
function isNewCampaign(prev: CampaignParams, curr: CampaignParams): boolean {
    // are all the keys in the current camapaign null?
    if (Object.values(curr).every(v => v === null)) {
        // don't start a new session if the current campaign is null
        return false;
    }
    return (
        prev.utm_source !== curr.utm_source ||
        prev.utm_medium !== curr.utm_medium ||
        prev.utm_campaign !== curr.utm_campaign ||
        prev.utm_content !== curr.utm_content ||
        prev.utm_term !== curr.utm_term
    );
}

/**
 * Validate the session info
 * @param {unknown | null} session - The session info from localStorage
 * @returns {boolean} True if the session info is valid, false otherwise
 */
function validateSessionInfo(session: unknown | null): session is SessionInfo {
    if (!session) {
        return false;
    }
    if (!(session as SessionInfo).sessionId) {
        return false;
    }
    if (!(session as SessionInfo).lastActivity) {
        return false;
    }
    return true;
}

/**
 * Get the session info from localStorage
 * @returns {SessionInfo | null} The session info
 */
function getSessionInfo(): SessionInfo | null {
    if (!window.localStorage) {
        return null;
    }
    try {
        const session = JSON.parse(localStorage.getItem(DIBS_SESSION) || 'null');
        if (!validateSessionInfo(session)) {
            return null;
        }
        return session;
    } catch {
        return null;
    }
}

/**
 * Set the session info in localStorage and cookie
 * @param {SessionInfo} info - The session info
 */
function setSessionInfo(info: SessionInfo): void {
    const serializedInfo = JSON.stringify(info);
    try {
        localStorage.setItem(DIBS_SESSION, serializedInfo);
        setSessionCookie(info);
    } catch {
        // silently fail
    }
}

function clearSessionInfo(): void {
    try {
        localStorage.removeItem(DIBS_SESSION);
        ClientCookieManager.remove(DIBS_SESSION);
    } catch {
        // silently fail
    }
}

function hasDibsSessionConsent(): boolean {
    return hasConsentForGroups(document.cookie, DIBS_SESSION_CONSENT_GROUPS);
}

function isSessionExpired(session: SessionInfo): boolean {
    return Date.now() - session.lastActivity > SESSION_TIMEOUT;
}

/**
 * Parses the `sessionId|lastActivity|{s,m,ca,co,t}` cookie written by `setSessionCookie`.
 */
function getCookieSessionInfo(): SessionInfo | null {
    const value = getCookieValue(document.cookie, DIBS_SESSION);
    if (!value) {
        return null;
    }
    // Split on only the first two pipes: a UTM value in the trailing campaign JSON is free-form
    // text from the URL and can itself contain a literal `|`, which would otherwise truncate it.
    const firstPipe = value.indexOf('|');
    const secondPipe = firstPipe === -1 ? -1 : value.indexOf('|', firstPipe + 1);
    if (firstPipe === -1 || secondPipe === -1) {
        return null;
    }
    const sessionId = value.slice(0, firstPipe);
    const lastActivityStr = value.slice(firstPipe + 1, secondPipe);
    const campaignStr = value.slice(secondPipe + 1);
    if (!SESSION_ID_PATTERN.test(sessionId || '')) {
        return null;
    }
    try {
        const { s, m, ca, co, t } = JSON.parse(campaignStr || '{}');
        const session = {
            sessionId,
            lastActivity: Number(lastActivityStr),
            campaign: {
                utm_source: s ?? null,
                utm_medium: m ?? null,
                utm_campaign: ca ?? null,
                utm_content: co ?? null,
                utm_term: t ?? null,
            },
        };
        return validateSessionInfo(session) ? session : null;
    } catch {
        return null;
    }
}

/**
 * localStorage is per origin, while the session cookie is shared across the cookie domain (www
 * and adminv2). When this origin has no localStorage record, adopt a still-live id from the
 * cookie instead of minting a new one, so both origins converge on one id instead of each
 * overwriting the other's cookie. `landingSite` is this page, since the adopting origin has no
 * record of where the cookie's session actually landed.
 */
function adoptSessionFromCookie(): SessionInfo | null {
    const fromCookie = getCookieSessionInfo();
    if (!fromCookie || isSessionExpired(fromCookie)) {
        return null;
    }
    return { ...fromCookie, landingSite: getCurrentLandingSite() };
}

/**
 * Create a new session
 * @param {string} sessionId - The session ID
 * @param {CampaignParams} campaign - The campaign params
 * @returns {SessionInfo} The session info
 */
function createNewSession(sessionId: string, campaign: CampaignParams): SessionInfo {
    return {
        sessionId,
        lastActivity: Date.now(),
        campaign,
        landingSite: getCurrentLandingSite(),
    };
}

/**
 * Initialize a new session or update existing and existing session. Without Performance consent
 * nothing is persisted, and any previously stored session is removed.
 */
export function initDibsSession(): void {
    if (!hasDibsSessionConsent()) {
        clearSessionInfo();
        return;
    }
    const currentCampaign = getCampaignParams();
    const storedSession = getSessionInfo();
    let session =
        (storedSession && !isSessionExpired(storedSession) ? storedSession : null) ||
        adoptSessionFromCookie();
    const isExpired = !session || isSessionExpired(session);
    // Check campaign change *only if* there is a valid session
    const isCampaignChanged =
        session && !isExpired && isNewCampaign(session.campaign, currentCampaign);

    if (!session || isExpired) {
        // No session or expired: create entirely new session with new ID
        session = createNewSession(generateSessionId(), currentCampaign);
    } else if (isCampaignChanged) {
        // Existing session, not expired, but campaign changed: Create new session record, reuse existing ID
        session = createNewSession(session.sessionId, currentCampaign);
    } else {
        // Existing session, not expired, campaign same: Just update last activity
        session.lastActivity = Date.now();
    }
    setSessionInfo(session);
}

/**
 * First landing page of the current session, for Elevar marketing.landing_site.
 */
export function getLandingSite(): string {
    return getSessionInfo()?.landingSite || getCurrentLandingSite();
}

/**
 * Shared join key between GA4 and Datadog RUM. `null` without Performance consent or before the
 * page has booted the session via `startDibsSession()`.
 */
export function getDibsSessionId(): string | null {
    if (typeof window === 'undefined' || !hasDibsSessionConsent()) {
        return null;
    }
    const session = getSessionInfo();
    return session && !isSessionExpired(session) ? session.sessionId : null;
}

let isSessionStarted = false;
let hasBooted = false;

export type LateDibsSessionHandler = (sessionId: string) => void;

const lateHandlers: LateDibsSessionHandler[] = [];

/** Buyer layout and Datadog each subscribe. Boot-time creation does not notify them. */
export function addLateDibsSessionHandler(handler: LateDibsSessionHandler): void {
    if (!lateHandlers.includes(handler)) {
        lateHandlers.push(handler);
    }
}

function notifyLateDibsSession(sessionId: string): void {
    lateHandlers.forEach((handler): void => {
        handler(sessionId);
    });
}

/**
 * `isSessionStarted` should only latch once a session is actually on disk. Consent may still be
 * missing (nothing persisted), in which case a later `startDibsSession()`/activity retry must be
 * able to try again instead of being permanently skipped.
 */
function markSessionStartedIfPersisted(): void {
    if (getSessionInfo()) {
        isSessionStarted = true;
    }
}

let consentPollId: ReturnType<typeof setInterval> | null = null;

function stopConsentPoll(): void {
    if (consentPollId !== null) {
        clearInterval(consentPollId);
        consentPollId = null;
    }
}

/**
 * First persist after boot (Performance consent was missing when `startDibsSession()` ran).
 * Notifies subscribers once. The boot persist itself does not, so `userTrackingReady` stays
 * the only dataLayer push when consent was already granted.
 */
function createSessionAfterBoot(): void {
    if (isSessionStarted || typeof window === 'undefined' || !hasDibsSessionConsent()) {
        return;
    }
    initDibsSession();
    const sessionId = getDibsSessionId();
    markSessionStartedIfPersisted();
    if (!sessionId) {
        return;
    }
    stopConsentPoll();
    notifyLateDibsSession(sessionId);
}

/**
 * Extends a live session on in-page activity, mirroring GA4's inactivity window. Only
 * `lastActivity` and the cookie expiry change: UTMs and `landingSite` are owned by
 * `initDibsSession()` at page boot. An already-expired session is left for the next boot to
 * replace.
 *
 * If there is no session at all (most likely because Performance consent was granted after boot,
 * so `startDibsSession()`'s initial `initDibsSession()` call persisted nothing), create one now.
 * This page's URL hasn't changed since boot, so the landing site/campaign are still correct for
 * it, unlike waiting for the next full navigation to create one against the wrong page.
 */
function touchDibsSession(): void {
    if (!hasDibsSessionConsent()) {
        clearSessionInfo();
        return;
    }
    const session = getSessionInfo();
    if (!session) {
        createSessionAfterBoot();
        return;
    }
    if (isSessionExpired(session)) {
        return;
    }
    setSessionInfo({ ...session, lastActivity: Date.now() });
}

let lastActivityReset = 0;
let areActivityListenersAttached = false;
let restoreConsentWatcher: (() => void) | null = null;

function attachConsentPoll(): void {
    if (consentPollId !== null) {
        return;
    }
    consentPollId = setInterval((): void => {
        if (isSessionStarted) {
            stopConsentPoll();
            return;
        }
        createSessionAfterBoot();
    }, 250);
}

type CookieOwner = object;

function findConfigurableCookie(
    doc: Document
): { owner: CookieOwner; descriptor: PropertyDescriptor } | null {
    let current: CookieOwner | null = doc;
    while (current) {
        const descriptor = Object.getOwnPropertyDescriptor(current, 'cookie');
        if (descriptor?.configurable && descriptor.get && descriptor.set) {
            return { owner: current, descriptor };
        }
        current = Object.getPrototypeOf(current) as CookieOwner | null;
    }
    return null;
}

/**
 * OneTrust writes `OptanonConsent` through `document.cookie` and does not emit a groups-updated
 * event that Ferrum listens for. Wrap the setter so a grant creates the session immediately,
 * ahead of the activity throttle. Poll only if the setter cannot be wrapped.
 */
function attachConsentWatcher(): void {
    if (restoreConsentWatcher || typeof document === 'undefined') {
        return;
    }
    const watched = findConfigurableCookie(document);
    if (!watched) {
        attachConsentPoll();
        return;
    }
    const { owner, descriptor } = watched;
    const get = descriptor.get as (this: Document) => string;
    const set = descriptor.set as (this: Document, value: string) => void;
    const consentAssignment = `${COOKIE_NAME_CONSENT_STATUS}=`;
    Object.defineProperty(owner, 'cookie', {
        configurable: true,
        enumerable: descriptor.enumerable ?? true,
        get(): string {
            return get.call(this as Document);
        },
        set(value: string): void {
            set.call(this as Document, value);
            if (value.includes(consentAssignment)) {
                createSessionAfterBoot();
            }
        },
    });
    restoreConsentWatcher = (): void => {
        Object.defineProperty(owner, 'cookie', descriptor);
        restoreConsentWatcher = null;
    };
}

function onActivity(): void {
    const now = Date.now();
    if (now - lastActivityReset < ACTIVITY_THROTTLE) {
        return;
    }
    lastActivityReset = now;
    touchDibsSession();
}

/**
 * Boots the session for the page and keeps it alive on activity. Call synchronously from every
 * client entry before anything reads `getDibsSessionId()`. Safe to call more than once: once a
 * session is persisted, later calls are a no-op, but if consent is missing on an earlier call
 * (nothing persisted), a later call (e.g. after OneTrust grants Performance consent) retries
 * instead of being permanently skipped.
 */
export function startDibsSession(): void {
    if (isSessionStarted || typeof window === 'undefined') {
        return;
    }
    const isFirstBoot = !hasBooted;
    hasBooted = true;
    initDibsSession();
    const sessionId = getDibsSessionId();
    markSessionStartedIfPersisted();

    if (!areActivityListenersAttached) {
        areActivityListenersAttached = true;
        lastActivityReset = Date.now();
        ACTIVITY_EVENTS.forEach(event =>
            window.addEventListener(event, onActivity, { capture: true, passive: true })
        );
    }

    if (!isSessionStarted) {
        attachConsentWatcher();
    }

    // A later startDibsSession() after consent was granted outside the cookie watcher.
    if (!isFirstBoot && sessionId) {
        notifyLateDibsSession(sessionId);
    }
}

/** ** FOR TESTING ** module state is page scoped in the browser. */
export function resetDibsSessionForTesting(): void {
    ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, onActivity, true));
    isSessionStarted = false;
    hasBooted = false;
    areActivityListenersAttached = false;
    lastActivityReset = 0;
    lateHandlers.length = 0;
    stopConsentPoll();
    restoreConsentWatcher?.();
}
